import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../i18n/translations';

export const PricingPage: React.FC = () => {
    const { language } = useLanguage();
    const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

    return (
        <div className="min-h-screen bg-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                {/* Header with back link */}
                <div className="mb-12">
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        {language === 'ru' ? 'На главную' : 'Back to home'}
                    </Link>
                </div>

                {/* Title */}
                <h1 className="text-4xl sm:text-5xl font-semibold mb-6 leading-tight" style={{ color: '#324966' }}>
                    {t('pricing.title', language) as string}
                </h1>

                {/* Description */}
                <p className="text-lg sm:text-xl text-gray-600 mb-16 leading-relaxed max-w-3xl">
                    {t('pricing.description', language) as string}
                </p>

                {/* Pricing Tiles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 items-stretch">
                    {/* Free Tile */}
                    <div className="bg-white border border-gray-200 rounded-lg p-8 hover:border-gray-300 transition-colors flex flex-col">
                        <h2 className="text-2xl font-semibold mb-2" style={{ color: '#324966' }}>
                            {t('pricing.free.title', language) as string}
                        </h2>
                        <div className="mb-4">
                            <span className="text-4xl font-semibold" style={{ color: '#324966' }}>
                                {t('pricing.free.price', language) as string}
                            </span>
                        </div>
                        <p className="text-gray-600 mb-6 leading-relaxed">
                            {t('pricing.free.description', language) as string}
                        </p>
                        <ul className="space-y-3 mb-8">
                            {(t('pricing.free.features', language) as string[]).map((feature, index) => (
                                <li key={index} className="flex items-start gap-3">
                                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#127BEB' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-gray-700 leading-relaxed">{feature}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-auto">
                            <a 
                                href="https://t.me/lingwisebot" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="block w-full text-center px-6 py-3 rounded-lg font-medium text-base text-white transition-colors duration-150 hover:opacity-90"
                                style={{ backgroundColor: '#127BEB' }}
                            >
                                {t('pricing.free.cta', language) as string}
                            </a>
                        </div>
                    </div>

                    {/* Paid Tile */}
                    <div className="bg-white border border-gray-200 rounded-lg p-8 hover:border-gray-300 transition-colors flex flex-col">
                        <h2 className="text-2xl font-semibold mb-2" style={{ color: '#324966' }}>
                            {t('pricing.paid.title', language) as string}
                        </h2>
                        
                        {/* Billing Toggle */}
                        <div className="mb-4">
                            <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 mb-4">
                                <button
                                    onClick={() => setBillingPeriod('monthly')}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                        billingPeriod === 'monthly'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                    aria-label={language === 'ru' ? 'Ежемесячно' : 'Monthly'}
                                >
                                    {t('pricing.paid.monthly', language) as string}
                                </button>
                                <button
                                    onClick={() => setBillingPeriod('yearly')}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                        billingPeriod === 'yearly'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                    aria-label={language === 'ru' ? 'Ежегодно' : 'Yearly'}
                                >
                                    {t('pricing.paid.yearly', language) as string}
                                </button>
                            </div>
                            <div>
                                <span className="text-4xl font-semibold" style={{ color: '#324966' }}>
                                    {billingPeriod === 'monthly' 
                                        ? (t('pricing.paid.priceMonthly', language) as string)
                                        : (t('pricing.paid.priceYearly', language) as string)
                                    }
                                </span>
                                <span className="text-gray-500 ml-2">
                                    {billingPeriod === 'monthly' 
                                        ? (t('pricing.paid.perMonth', language) as string)
                                        : (t('pricing.paid.perYear', language) as string)
                                    }
                                </span>
                            </div>
                            {billingPeriod === 'yearly' && (
                                <p className="text-sm text-gray-600 mt-2">
                                    {t('pricing.paid.yearlyNote', language) as string}
                                </p>
                            )}
                        </div>

                        <p className="text-gray-600 mb-6 leading-relaxed">
                            {t('pricing.paid.description', language) as string}
                        </p>
                        <ul className="space-y-3 mb-8">
                            {(t('pricing.paid.features', language) as string[]).map((feature, index) => (
                                <li key={index} className="flex items-start gap-3">
                                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#127BEB' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-gray-700 leading-relaxed">{feature}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-auto">
                            <a 
                                href="https://t.me/lingwisebot" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="block w-full text-center px-6 py-3 rounded-lg font-medium text-base text-white transition-colors duration-150 hover:opacity-90"
                                style={{ backgroundColor: '#127BEB' }}
                            >
                                {t('pricing.paid.cta', language) as string}
                            </a>
                        </div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="mt-16 pt-16 border-t border-gray-200">
                    <h2 className="text-2xl font-semibold mb-8" style={{ color: '#324966' }}>
                        {t('pricing.faq.title', language) as string}
                    </h2>
                    <div className="space-y-6">
                        {(t('pricing.faq.items', language) as any[]).map((item, index) => (
                            <div key={index}>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {item.question}
                                </h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {item.answer}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
